import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";
import type { PayrollExternalTuitionDraft } from "../../types";

type ExternalTuitionPaymentFormProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  initialValue?: PayrollExternalTuitionDraft | null;
  onSave?: (input: PayrollExternalTuitionDraft) => void | Promise<void>;
};

export function ExternalTuitionPaymentForm({
  isOpen,
  onClose,
  employeeName,
  initialValue,
  onSave,
}: ExternalTuitionPaymentFormProps) {
  const [receivingSchoolName, setReceivingSchoolName] = useState("");
  const [childName, setChildName] = useState("");
  const [annualAmount, setAnnualAmount] = useState("");
  const [taxTreatment, setTaxTreatment] =
    useState<NonNullable<PayrollExternalTuitionDraft["taxTreatment"]>>(
      "Tax-Exempt",
    );
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setReceivingSchoolName(initialValue?.receivingSchoolName ?? "");
    setChildName(initialValue?.childName ?? "");
    setAnnualAmount(
      initialValue?.annualAmount == null
        ? ""
        : String(initialValue.annualAmount),
    );
    setTaxTreatment(initialValue?.taxTreatment ?? "Tax-Exempt");
    setNotes(initialValue?.notes ?? "");
  }, [initialValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const isValid =
    receivingSchoolName !== "" &&
    childName !== "" &&
    annualAmount !== "" &&
    Number(annualAmount) > 0;

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    await onSave?.({
      ...initialValue,
      receivingSchoolName,
      childName,
      annualAmount: Number(annualAmount),
      taxTreatment,
      notes: notes || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              {initialValue?.id ? "Edit External Tuition Payment" : "Add External Tuition Payment"}
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Receiving School Name
            </label>
            <input
              type="text"
              value={receivingSchoolName}
              onChange={(event) => setReceivingSchoolName(event.target.value)}
              placeholder="e.g. Torah Academy of Bergen County"
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Child Name
            </label>
            <input
              type="text"
              value={childName}
              onChange={(event) => setChildName(event.target.value)}
              placeholder="e.g. Rachel Levy"
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Annual Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={annualAmount}
                  onChange={(event) => setAnnualAmount(event.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Tax Treatment
              </label>
              <select
                value={taxTreatment}
                onChange={(event) =>
                  setTaxTreatment(
                    event.target
                      .value as NonNullable<
                      PayrollExternalTuitionDraft["taxTreatment"]
                    >,
                  )
                }
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="Tax-Exempt">Tax-Exempt</option>
                <option value="Taxable">Taxable</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              className="w-full resize-none rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={() => void handleSave()}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Payment
          </button>
        </div>
      </div>
    </div>
  );
}
